import React, { useState, useEffect, useContext } from "react";
import "../../App.css"; // Import global styles
import { FaSearch, FaPlus, FaTimes, FaEllipsisV } from "react-icons/fa"; // Import icons
import AddTutorialVideo from "./AddTutorialVideo"; // Import AddTutorialVideo component
import { UserContext } from "../context/UserContext"; // Import UserContext for user authentication

const TutorialList = () => {
  const [searchQuery, setSearchQuery] = useState(""); // State for search input
  const [isAddVideoOpen, setIsAddVideoOpen] = useState(false); // State to toggle AddTutorialVideo popup
  const [videos, setVideos] = useState([]); // Initialize as an empty array
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [message, setMessage] = useState(""); // Message for login prompt
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024); // State to track screen size
  const [selectedVideo, setSelectedVideo] = useState(null); // State to track clicked video
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [initialDistance, setInitialDistance] = useState(null);

  const { isLoggedIn, emailId } = useContext(UserContext); // Fetch user login status and email

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024); // Update mobile state based on screen width
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize); // Cleanup on unmount
  }, []);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch("/api/tutorialvideos"); // Replace with actual API endpoint
        if (response.ok) {
          const data = await response.json();
          setVideos(data); // Update state with fetched data
        } else {
          console.error("Failed to fetch tutorial videos");
        }
      } catch (error) {
        console.error("Error fetching tutorial videos:", error);
      } finally {
        setIsLoading(false); // Stop loading once data is fetched
      }
    };

    fetchVideos(); // Initial fetch

    // Set up an interval to fetch tutorial videos every 5 seconds
    const intervalId = setInterval(fetchVideos, 5000);

    return () => clearInterval(intervalId); // Cleanup interval
  }, []);

  const handleAddVideoClick = () => {
    if (isLoggedIn && emailId === "urvote_tutorials@urcommunity.com.au") {
      setIsAddVideoOpen(true);
      setMessage("");
    } else {
      setMessage("Only authorized users can add tutorial videos.");
    }
  };

  const filteredVideos = videos.filter((video) =>
    video.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getYouTubeVideoId = (url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : '';
  };

  const handleTouchStart = (e) => {
    if (window.innerWidth <= 926) {
      if (e.touches.length === 2) {
        const distance = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        setInitialDistance(distance);
      } else {
        const touch = e.touches[0];
        setStartPosition({
          x: touch.clientX - position.x,
          y: touch.clientY - position.y
        });
      }
    }
  };

  const handleTouchMove = (e) => {
    if (window.innerWidth <= 926) {
      e.preventDefault();
      if (e.touches.length === 2) {
        const distance = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        if (initialDistance) {
          const newScale = scale * (distance / initialDistance);
          setScale(Math.min(Math.max(0.5, newScale), 2));
          setInitialDistance(distance);
        }
      } else {
        const touch = e.touches[0];
        setPosition({
          x: touch.clientX - startPosition.x,
          y: touch.clientY - startPosition.y
        });
      }
    }
  };

  const handleTouchEnd = () => {
    setInitialDistance(null);
  };

  return (
    <div className="tutorial-list-container" style={{ 
      padding: "10px",
      height: "100%",
      overflow: "hidden"
    }}>
      {/* Subscribe Button */}
      <div style={{ 
        display: "flex", 
        justifyContent: "center",
        marginBottom: "15px",
        width: "100%",
        backgroundColor: "#fff",
        zIndex: 1
      }}>
        <button
          onClick={() => window.open('https://www.youtube.com/@UrCommunityLtd?sub_confirmation=1', '_blank')}
          style={{
            background: "black",
            color: "#fff",
            border: "none",
            padding: "10px 20px",
            borderRadius: "50px",
            fontSize: "14px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            transition: "transform 0.2s ease",
            minWidth: "180px",
            justifyContent: "center"
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          Subscribe on YouTube
        </button>
      </div>

      {/* Search bar and Add button */}
      <div
        className="search-add-container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
          backgroundColor: "#fff",
          zIndex: 1,
          padding: "10px 0"
        }}
      >
        {/* Search Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            width: "300px",
            height: "40px",
            background: "linear-gradient(to right, #007bff, #00d4ff)",
            borderRadius: "50px",
            padding: "10px",
          }}
        >
          <FaSearch
            style={{
              fontSize: "18px",
              color: "#fff",
              marginRight: "10px",
            }}
          />
          <input
            type="text"
            placeholder="Search tutorials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              color: "#fff",
              fontSize: "14px",
              caretColor: "#fff",
            }}
            className="search-input-placeholder"
          />
        </div>

        {/* Add Button (only for authorized email) */}
        {emailId === "urvote_tutorials@urcommunity.com.au" && (
          <button
            onClick={handleAddVideoClick}
            style={{
              background: "linear-gradient(to right, #007bff, #00d4ff)",
              color: "#fff",
              border: "none",
              padding: "10px",
              borderRadius: "50%",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <FaPlus />
          </button>
        )}
      </div>

      {/* Login Prompt */}
      {message && (
        <div style={{ marginBottom: "20px", color: "red", fontSize: "14px", fontWeight: "bold" }}>
          {message}
        </div>
      )}

      {/* Tutorial Video List - Scrollable */}
      <div className="tutorial-list" style={{ 
        display: "grid", 
        gap: "20px",
        maxHeight: "calc(100vh - 180px)",
        overflowY: "auto",
        padding: "10px 10px 160px 0",
        marginRight: "5px"
      }}>
        {isLoading ? (
          <p>Loading tutorial videos...</p>
        ) : (
          filteredVideos.map((video) => (
            <div
              key={video.id}
              className="tutorial-card"
              onClick={() => setSelectedVideo(video)} // Open pop-up on click
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "15px",
                padding: "10px",
                backgroundColor: "#fff",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                alignItems: isMobile ? "center" : "flex-start",
                width: isMobile ? "100%" : "380px",
                cursor: "pointer",
              }}
            >
              <div className="tutorial-thumbnail" 
                style={{ 
                  width: isMobile ? "100%" : "150px",
                  height: isMobile ? "auto" : "100px",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}>
                {video.video_url?.includes('youtube.com') || video.video_url?.includes('youtu.be') ? (
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(video.video_url)}`}
                    frameBorder="0"
                    style={{ 
                      width: "100%", 
                      height: "100%",
                      minHeight: "100px",
                      objectFit: "cover"
                    }}
                  />
                ) : (
                  <video style={{ width: "100%", height: "100%", objectFit: "cover" }}>
                    <source src={video.video_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
              {/* Video Info */}
              <div className="tutorial-info" style={{ flex: "1", textAlign: isMobile ? "center" : "left" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h5 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "4px" }}>{video.title}</h5>
                </div>
                <p style={{ fontSize: "14px", color: "#555", marginBottom: "0" }}>{video.description}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Video Pop-up Modal */}
      {selectedVideo && (
        <div className="video-modal" style={{
          position: "fixed",
          top: "0",
          left: "0",
          width: "90vw",
          height: "100vh",
          display: "flex",
          justifyContent: window.innerWidth <= 926 ? "center" : "flex-start",
          alignItems: window.innerWidth <= 926 ? "center" : "flex-start",
          zIndex: "1000",
          // backgroundColor: "rgba(0, 0, 0, 0.8)",
          paddingLeft: window.innerWidth <= 926 ? "0" : "1%",
          paddingTop: window.innerWidth <= 926 ? "0" : "10%",
          paddingBottom: window.innerWidth <= 926 ? "0" : "5%",
          paddingRight: window.innerWidth <= 926 ? "100%" : "0",
          touchAction: "none"
        }}>
          {/* Close Button */}
          <div style={{
            position: "fixed",
            top: window.innerWidth <= 926 ? "20%" : "20%",
            right: window.innerWidth <= 926 ? "0%" : "29%",
            backgroundColor: window.innerWidth <= 926 ? "white" : "rgba(0, 0, 0, 0.8)",
            borderRadius: "50%",
            padding: window.innerWidth <= 926 ? "15px" : "8px",
            cursor: "pointer",
            zIndex: 20000, // Increased z-index to ensure visibility
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: window.innerWidth <= 926 ? "0 2px 5px rgba(0,0,0,0.2)" : "none"
          }}>
            <FaTimes 
              onClick={() => setSelectedVideo(null)} 
              style={{ 
                color: window.innerWidth <= 926 ? "#000" : "#fff",
                fontSize: window.innerWidth <= 926 ? "28px" : "20px",
              }} 
            />
          </div>

          <div 
            style={{
              backgroundColor: "#fff",
              padding: window.innerWidth <= 926 ? "15px" : "12px",
              borderRadius: "10px",
              position: window.innerWidth <= 926 ? "fixed" : "relative",
              width: window.innerWidth <= 926 ? "130%" : "100%",
              maxWidth: window.innerWidth <= 926 ? "none" : "1000px",
              height: window.innerWidth <= 926 ? "40%" : "100%", // Increased mobile height to 40%
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
              top: window.innerWidth <= 926 ? "50%" : "auto",
              left: window.innerWidth <= 926 ? "30%" : "auto",
              transform: window.innerWidth <= 926 ? "translate(-50%, -50%)" : "none",
              margin: 0,
              cursor: "default",
              userSelect: "none",
              WebkitUserSelect: "none",
              touchAction: "none"
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Video Box */}
            <div style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              backgroundColor: "#000",
              borderRadius: "10px",
              marginTop: window.innerWidth <= 926 ? "0" : "10px"
            }}>
              {selectedVideo.video_url?.includes('youtube.com') || selectedVideo.video_url?.includes('youtu.be') ? (
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedVideo.video_url)}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ 
                    borderRadius: "10px",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    maxHeight: "100%"
                  }}
                ></iframe>
              ) : (
                <video 
                  controls 
                  style={{ 
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: "10px",
                    maxHeight: "100%"
                  }}
                >
                  <source src={selectedVideo.video_url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Video Popup */}
      {isAddVideoOpen && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
          }}
        >
      <AddTutorialVideo onClose={() => setIsAddVideoOpen(false)} />
    </div>
    )}
  </div>
  );
};

export default TutorialList;
